import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.user);
      return response.user;
    } catch (error) {
      // Account disabled or token invalid — force logout
      if (error?.status === 403 || error?.status === 401) {
        logout();
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    fetchUser();

    // Poll every 30s so feature flag / account changes apply without re-login
    pollRef.current = setInterval(() => {
      if (localStorage.getItem('token')) {
        fetchUser();
      }
    }, 30000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/api/auth/login', { email, password });
    const { token, user } = response;
    localStorage.setItem('token', token);
    setUser(user);

    // Start polling after login
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (localStorage.getItem('token')) fetchUser();
    }, 30000);

    return response;
  };

  const register = async (email, password, companyName) => {
    const response = await api.post('/api/auth/register', { email, password, companyName });
    const { token, user } = response;
    localStorage.setItem('token', token);
    setUser(user);
    return response;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    fetchUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};